// =====================================================================
// My Agency V2 — design tokens (ARTBOARD-SPEC.md §2, verbatim values).
//
// Scope: My Agency surfaces ONLY (agents / invite / players / partners
// + their mobile boards). Never import these into non-My-Agency
// surfaces — no global restyling (Brief Rule 5).
//
// The repo themes via Tailwind arbitrary values (`bg-[#2283FF]`), so
// most V2 components inline the hex values in their class strings.
// This module is the single reference (and is used where values are
// computed or passed as props/styles). Keep in sync with the spec —
// the artboard wins (Brief Rule 1).
// =====================================================================

export const COLOR = {
  brand: "#2283FF",
  brandHover: "#1670E6",
  link: "#1668D9",
  linkHover: "#0F56BC",
  brandBg: "#EAF3FF", // Admin pill bg, active count pill bg, tinted icon circles
  brandBgHover: "#DCEBFF",
  brandSelectedBg: "#F5F9FF", // selected list row / radio card bg
  brandBorderHover: "#B9D8FF", // card hover, active filter pill border
  brandBorderInfo: "#D6E7FF", // info box border
  brandDisabled: "#A8C8F0", // disabled primary bg

  textStrong: "#0F172A",
  textMid: "#334155", // labels, menu items
  textBody: "#475569", // body, secondary btn text, contact rows
  textSoft: "#64748B", // subtitles, inactive tabs
  textMuted: "#94A3B8", // placeholders, helpers, footnotes, muted "0"
  textFaint: "#C4CEDA", // empty placeholders, "—", disabled icons, dashed borders

  canvas: "#F4F6FA", // screen bg
  doc: "#EDF0F5",

  cardBorder: "#E3E9F1",
  inputBorder: "#DCE3EC",
  inputBorderHover: "#B9C6D6",
  chipBorder: "#EBF0F6", // inner-chip border
  divider: "#EDF1F6",
  dividerList: "#F0F3F8",
  dividerSheet: "#F5F7FA",
  popoverBorder: "#E4E9F0",
  segmentedBg: "#E7EDF4",

  danger: "#DC2626",
  dangerHover: "#B91C1C",
  dangerText: "#B91C1C",
  dangerBg: "#FEF2F2",
  dangerBorder: "#FECACA",
  dangerPillBg: "#FEE2E2",

  warningDot: "#F59E0B",
  warningPillBg: "#FEF3C7",
  warningPillText: "#92400E",
  warningBannerBg: "#FFFBEB",
  warningBannerBorder: "#FDE68A",
  warningStrong: "#B45309",

  successDot: "#22C55E",
  successStroke: "#16A34A",
  successText: "#15803D",
  successBg: "#ECFDF5",
  successValidBorder: "#86D3A5",
  successTint: "#DCFCE7",

  markBg: "#FFF1B8", // <mark> search highlight

  skeletonStrong: "#E4EAF1",
  skeletonLight: "#EDF1F6",
  skeletonChip: "#F0F4F9",

  tooltipBg: "#0F172A",
};

export const SHADOW = {
  card: "0 1px 2px rgba(15,23,42,.05)",
  cardHover: "0 4px 14px rgba(15,23,42,.08)",
  btnPrimary: "0 1px 2px rgba(34,131,255,.35)",
  btnDanger: "0 1px 2px rgba(220,38,38,.35)",
  fab: "0 8px 24px rgba(34,131,255,.4)",
  focusRing: "0 0 0 3px rgba(34,131,255,.12)",
  errorRing: "0 0 0 3px rgba(220,38,38,.1)",
  modal: "0 24px 64px rgba(15,23,42,.3)",
  popover: "0 12px 32px rgba(15,23,42,.16)",
  dropdown: "0 12px 32px rgba(15,23,42,.14)",
  tooltip: "0 8px 24px rgba(15,23,42,.3)",
  sheet: "0 -12px 40px rgba(15,23,42,.25)",
  segActive: "0 1px 2px rgba(15,23,42,.08)",
};

export const OVERLAY = {
  modal: "rgba(15,23,42,.45)", // desktop modal scrim
  sheet: "rgba(15,23,42,.5)", // mobile sheet scrim
};

// Radii scale (spec §2 "Radii scale") — px values.
export const RADIUS = {
  pill: 999,
  flag: 3,
  copyBtn: 6,
  menuItem: 7,
  iconBtn: 8,
  input: 9,
  segmented: 10,
  popover: 11,
  card: 12,
  modal: 14,
  mobileDialog: 16,
  sheet: "22px 22px 0 0",
};

// Font family for the V2 surfaces: 'Instrument Sans' per the artboard
// (loaded in public/index.html alongside Inter). Applied via the
// Tailwind `font-tpv2` utility (tailwind.config.js) on the V2 page
// container only.
export const FONT_FAMILY = "'Instrument Sans', system-ui, sans-serif";

// Avatar initial palettes (bg / text) — ILLUSTRATIVE assignment in the
// artboard; rotate deterministically per entity (Avatar.js).
export const AVATAR_PALETTES = [
  { bg: "#DBEAFE", text: "#1D4ED8" },
  { bg: "#CCFBF1", text: "#0F766E" },
  { bg: "#FEF3C7", text: "#B45309" },
  { bg: "#EDE9FE", text: "#6D28D9" },
  { bg: "#FCE7F3", text: "#BE185D" },
  { bg: "#E0F2FE", text: "#0369A1" },
  { bg: "#DCFCE7", text: "#15803D" },
  { bg: "#F3EFFF", text: "#6D4FC2" },
];

// Pending / greyed avatar (dashed-invite rows, pending partner cards).
export const AVATAR_PALETTE_PENDING = { bg: "#EAEFF5", text: "#94A3B8" };
